import { useEffect, useState } from 'react'
import { httpsCallable, getFunctions } from 'firebase/functions'
import Graph from '../components/Graph'
import useGetAnalytics from '../hooks/useGetAnalytics'
import useGetApiKeys from '../hooks/useGetApiKeys'
import EndpointList from '../components/EndpointList'
import FunnelChart from '../components/FunnelChart'
import { startOfDay, endOfDay, parseISO } from 'date-fns'
import InputField from '../components/InputField'
import { thisWeek, lastWeek, thisMonth, lastMonth, thisYear } from '../utils/date-utils.js'
import Button from '../components/Button'
import SelectBox from '../components/SelectBox'
import BouncingDots from '../components/BouncingDots'
import useActivityLog from '../hooks/useActivityLog'
import GuidedTour from '../components/GuidedTour'

// Dummy Data to build out the page and UI first

const DATE_SHORTCUTS = [
  { label: 'This Week', fn: thisWeek },
  { label: 'This Month', fn: thisMonth },
  { label: 'Last Week', fn: lastWeek },
  { label: 'Last Month', fn: lastMonth },
  { label: 'This Year', fn: thisYear }
]

const adminEnvs = ['all', 'test', 'dev', 'uat', 'prod']
const customerEnvs = ['uat', 'prod']
let dateSetDebounce

const AnalyticsPage = ({ customerKeys, role, customer }) => {
  // Key Related state
  const { logAction } = useActivityLog()
  const envs = role === 'customer' ? customerEnvs : adminEnvs

  // Determine which APIs this customer has access to
  const isMedipro = role === 'admin' || customer?.isMediproCustomer !== false
  const isAcapro = role === 'admin' || customer?.isAcaproCustomer === true
  const availableApis = []
  if (isMedipro) availableApis.push('MediPro')
  if (isAcapro) availableApis.push('ACAPro')

  // Map of API key id -> human-readable name, for resolving customerKeyId in
  // analytics rows (e.g. the funnel export) to a friendly label.
  const keyNameMap = (customerKeys || []).reduce((acc, k) => {
    acc[k.id] = k.name
    return acc
  }, {})

  const [api, setApi] = useState(availableApis[0] || 'MediPro')
  const [env, setEnv] = useState(envs[0])

  // Filter keys based on API selection
  const filteredKeys = customerKeys.filter((k) => {
    if (k.id === 'all') return true
    if (api === 'ACAPro') return k.name.toUpperCase().includes('ACA')
    return !k.name.toUpperCase().includes('ACA')
  })
  const [key, setKey] = useState(filteredKeys[0])
  // Timeframe and date picker state
  const todaysDate = endOfDay(new Date())
  const previousDate = startOfDay(todaysDate)

  const [endDate, setEndDate] = useState()
  const [startDate, setStartDate] = useState()

  // Misc — hardcoded values
  const graphType = 'bar'
  const grouping = 'endpoint'

  const {
    loading: graphLoading,
    data: graphData,
    listData,
    detailData,
    error,
    settings,
    update,
    refreshData
  } = useGetAnalytics(key.id, { today: todaysDate.toISOString(), previous: previousDate.toISOString() }, env, grouping, role, api)

  if (error) {
    console.error(error)
  }

  // Updates timeframe on state variables updates

  useEffect(() => {
    clearTimeout(dateSetDebounce)
    // from is start of interval(startDate) to is end of interval(endDate)
    if (startDate !== undefined && endDate !== undefined) {
      dateSetDebounce = setTimeout(() => {
        const currentDate = endOfDay(parseISO(endDate))
        currentDate.setUTCHours(0, 0, 0, 0)
        const previousDate = startOfDay(parseISO(startDate))
        previousDate.setHours(0, 0, 0, 0)
        const newTimeFrame = { today: currentDate.toISOString(), previous: previousDate.toISOString() }

        update({ timeFrame: newTimeFrame })
      }, 100)
    }
    // update in the dependency array is causing continous rerender removing it for development
    // eslint-disable-next-line
  }, [startDate, endDate ])

  // Function Definitions
  const updateKey = (e) => {
    e.preventDefault()
    setKey(e.target.value)
    update({ key: e.target.value })
  }

  const updateApi = (e) => {
    e.preventDefault()
    const newApi = e.target.value
    setApi(newApi)
    // Reset key to first filtered option
    const newFiltered = customerKeys.filter((k) => {
      if (k.id === 'all') return true
      if (newApi === 'ACAPro') return k.name.toUpperCase().includes('ACA')
      return !k.name.toUpperCase().includes('ACA')
    })
    const newKey = newFiltered[0]
    setKey(newKey)
    update({ key: newKey?.id, api: newApi })
  }

  const updateEnv = (e) => {
    e.preventDefault()
    setEnv(e.target.value)
    update({ env: e.target.value })
  }

  const handleStartDate = (e) => {
    e.preventDefault()
    // These are disabled currently right now. More work is needed to make the useGetAnalytics function more modifiable
    // setTimeFrameType('picked')
    setStartDate(e.target.value)
  }

  const handleEndDate = (e) => {
    e.preventDefault()
    // These are disabled currently right now. More work is needed to make the useGetAnalytics function more modifiable
    // setTimeFrameType('picked')
    setEndDate(e.target.value)
  }

  const setDates = (fn) => {
    const { startDate: sd, endDate: ed } = fn()
    setStartDate(sd)
    setEndDate(ed)
  }

  // const updateTimeFrame = (e) => {
  //   e.preventDefault()
  //   setGraphLoading(true)
  //   setTimeFrameType('selected')
  //   if (e.target.dataset.time !== undefined) {
  //     const time = parseInt(e.target.dataset.time)
  //     setSelectedTime(time)
  //   }
  //   setTimeout(() => {
  //     setGraphLoading(false)
  //   }, 250)
  // }

  return (
    <div className='w-full'>
      <GuidedTour
        tourKey='api_usage'
        steps={[
          { target: '[data-tour="usage-filters"]', title: 'Filters', content: 'Select an API key, environment, and date range to filter your usage data.' },
          { target: '[data-tour="usage-run"]', title: 'Run Report', content: 'Click Run Report to fetch analytics data for your selected filters.' },
          { target: '[data-tour="usage-presets"]', title: 'Date Presets', content: 'Use these shortcuts to quickly set common date ranges.' }
        ]}
      />
      <div className='px-4'>
        <h1 className='text-4xl mb-4'>API Usage</h1>
        <div className='flex flex-wrap items-end gap-3 mb-2' data-tour='usage-filters'>
          <SelectBox containerClassName='mb-0' labelClassName='text-left' value={api} label='API' name='api-selection' onChange={updateApi}>
            {availableApis.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </SelectBox>
          <SelectBox containerClassName='mb-0' labelClassName='text-left' value={key} label='API Key' name='key-selection' onChange={updateKey}>
            {filteredKeys.map((key) => {
              return (<option key={key.id} value={key.id}>{key.name}</option>)
            })}
          </SelectBox>
          <SelectBox containerClassName='mb-0' labelClassName='text-left' value={env} label='Environment' name='key-selection' onChange={updateEnv}>
            {envs.map((e) => {
              return (<option key={e} value={e}>{e}</option>)
            })}
          </SelectBox>
          <InputField
            containerClassName='mb-0'
            labelClassName='text-left'
            labelFor='startDate'
            type='date'
            id='startDate'
            name='startDate'
            max={endDate}
            onChange={handleStartDate}
            value={startDate}
          >Start Date
          </InputField>
          <InputField
            containerClassName='mb-0'
            labelClassName='text-left'
            labelFor='endDate'
            type='date'
            id='endDate'
            name='endDate'
            min={startDate}
            onChange={handleEndDate}
            value={endDate}
          >End Date
          </InputField>
          <span data-tour='usage-run'>
            <Button className='m-0 py-2.5' onClick={() => { refreshData(); if (role === 'admin') { logAction('run_analytics_report', { apiKey: key, environment: env, startDate, endDate }) } }}>Run Report</Button>
          </span>
        </div>
        <div className='text-sm flex items-center gap-2 mb-4' data-tour='usage-presets'>
          <span className='text-gray-500'>Date Presets:</span>
          {DATE_SHORTCUTS.map((ds, index) => {
            return <button key={index} className='text-blue-6 hover:underline' onClick={() => setDates(ds.fn)}>{ds.label}</button>
          })}
        </div>
      </div>
      <div className='w-full'>
        {/* <div className='flex flex-row bg-blue-4 p-2 m-2 max-w-4xl justify-between rounded-md' onClick={updateTimeFrame}>
          <p>Show last: </p>
          <span className={`${selectedTime === 1 ? 'underline font-bold' : 'text-black'}`} data-time={1}>24 hours</span>
          <span className={`${selectedTime === 7 ? 'underline font-bold' : 'text-black'}`} data-time={7}>7 days</span>
          <span className={`${selectedTime === 30 ? 'underline font-bold' : 'text-black'}`} data-time={30}>30 days</span>
          <span className={`${selectedTime === 365 ? 'underline font-bold' : 'text-black'}`} data-time={365}>12 months</span>
        </div> */}
        {graphLoading && (
          <BouncingDots message='Loading analytics data...' />
        )}
        {!graphLoading && role === 'admin' && detailData && detailData.length > 0 && <FunnelChart detailData={detailData} />}
        {!graphLoading && graphData && graphType === 'bar' && <Graph graphData={graphData} timeFrame={settings.timeFrame} />}
        {!graphLoading && <EndpointList listData={listData} detailData={detailData} timeFrame={settings.timeFrame} apiKey={key} environment={env} keyNames={keyNameMap} isAdmin={role === 'admin'} />}
      </div>
    </div>
  )
}

const LoadingKeys = ({ role, customer }) => {
  const { loading: adminKeysLoading, keys: adminKeys } = useGetApiKeys(role === 'customer')
  const [customerKeys, setCustomerKeys] = useState([])
  const [customerKeysLoading, setCustomerKeysLoading] = useState(role === 'customer')

  useEffect(() => {
    if (role !== 'customer' || !customer?.apiKeyIds?.length) {
      setCustomerKeysLoading(false)
      return
    }

    const fetchCustomerKeys = async () => {
      try {
        const getCustomerKeysFn = httpsCallable(getFunctions(), 'getCustomerKeys')
        const res = await getCustomerKeysFn({ apiKeyIds: customer.apiKeyIds })
        const keys = (res.data.keys || []).map((k) => ({ id: k.id, name: k.name }))
        setCustomerKeys(keys)
      } catch (err) {
        console.error('Error fetching customer keys for analytics:', err)
        setCustomerKeys([])
      }
      setCustomerKeysLoading(false)
    }

    fetchCustomerKeys()
  }, [role, customer])

  const isLoading = role === 'customer' ? customerKeysLoading : adminKeysLoading

  if (isLoading) {
    return (
      <div className='flex flex-col items-center justify-center py-16 gap-3'>
        <div className='w-10 h-10 border-t-4 border-b-4 border-blue-400 rounded-full animate-spin' />
      </div>
    )
  }

  const keysToUse = role === 'customer' ? customerKeys : adminKeys

  return <AnalyticsPage customerKeys={keysToUse} role={role} customer={customer} />
}

export default LoadingKeys
